/**
 * Marqueurs personnalisés : ajout, édition, déplacement, liste.
 *
 * Portage de `CustomMarkerController.kt` et des interactions carte de
 * `MapManager.kt`.
 *
 * Quatre parcours s'articulent autour d'une même difficulté : sur une boucle
 * ou un aller-retour, un point de l'écran correspond à **plusieurs** points
 * kilométriques. Chaque fois qu'une position est désignée du doigt, l'ambiguïté
 * doit être levée par l'utilisateur plutôt que tranchée arbitrairement.
 */

import type { Map as MapLibreMap } from 'maplibre-gl'

import { metersPerPixel } from '../core/geo'
import type { DistanceCandidate, GpxTrack } from '../core/track'
import {
  DERIVATION_SYMBOL_LEFT,
  DERIVATION_SYMBOL_RIGHT,
  MARKER_TYPE_DERIVATION,
  MARKER_TYPE_WAYOUT,
  PREDEFINED_MARKER_TYPES,
  createCustomMarker,
  type CustomMarker,
} from '../core/types'
import type { Translator, StringKey } from '../i18n'
import { chooseDialog, confirmDialog, setDialogTitle, showDialog } from './dialogs'
import { createIcon } from './icons'
import type { Shell } from './shell'

/**
 * Tolérance de l'appui long, en pixels.
 *
 * Convertie en mètres au sol selon le zoom : toucher « la trace » doit rester
 * aussi facile de près que de loin.
 */
const LONG_PRESS_TOLERANCE_PX = 30

/** Tolérance du clic sur un marqueur, en pixels. */
const CLICK_TOLERANCE_PX = 20

/**
 * Entrée du sélecteur de type qui n'est **pas** un marqueur : elle pose le
 * départ réel de la trace.
 *
 * Jamais un `type` de marqueur, jamais enregistrée : l'ajouter à
 * `PREDEFINED_MARKER_TYPES` la ferait exporter dans le GPX et apparaître parmi
 * les symboles personnalisables des réglages. Elle n'existe que le temps du
 * dialogue ouvert par un appui long, seul geste qui désigne un point de la
 * trace — la liste des marqueurs, elle, ne parle que de marqueurs.
 */
const PSEUDO_TYPE_REAL_START = '__realStart__'

/**
 * Ce que le dialogue d'ajout doit rendre à ce qui l'a ouvert.
 *
 * La liste des marqueurs reste ouverte sous lui : elle a besoin de savoir
 * quand se reconstruire, et quand s'effacer pour libérer la carte.
 */
/**
 * Ce qu'un dialogue ouvert depuis la liste doit à la liste.
 *
 * Ajout, édition et suppression y passent : la liste reste ouverte dessous et
 * se reconstruit quand ils rendent la main, comme le fait
 * `CustomMarkerController` en empilant ses dialogues sur elle.
 */
interface FlowCallbacks {
  /** La suite se joue sur la carte : ce qui la couvre doit se retirer. */
  readonly onPickFlow?: () => void
  /** Le marqueur est posé, réglé ou retiré : la liste ouverte se reconstruit. */
  readonly onDone?: () => void
}

export interface MarkerControllerDeps {
  readonly map: MapLibreMap
  readonly shell: Shell
  readonly translator: Translator
  /** Traces dans l'ordre du tiroir. */
  readonly tracks: () => readonly GpxTrack[]
  /** Redessine la trace et enregistre les modifications. */
  readonly onTrackChanged: (track: GpxTrack) => void
  /**
   * Ouvre les réglages kilométriques de la trace.
   *
   * Le bouton « régler » d'un marqueur de départ n'a pas de dialogue propre :
   * il mène au même écran que le hub, où le départ réel se saisit au mètre.
   */
  readonly onEditRealStart: (track: GpxTrack) => void
  /**
   * Symbole d'un type prédéfini, surcharge de l'utilisateur comprise.
   *
   * Le symbole est copié dans le marqueur à sa création : le proposer ici est
   * le seul endroit où la surcharge intervient, un marqueur déjà posé gardant
   * le sien.
   */
  readonly symbolForType: (typeId: string) => string
  /**
   * Actions portées par le contrôleur des détours.
   *
   * Un marqueur de type « way out » n'est que le reflet d'un détour : le
   * modifier ici ne survivrait pas à sa reconstruction. Toutes ces actions
   * visent donc le détour source.
   */
  readonly ways: WayActions
}

export interface WayActions {
  /** Le type « way out » du dialogue d'ajout crée un détour, pas un marqueur. */
  readonly create: (track: GpxTrack, exitKm: number, name: string) => void
  readonly edit: (
    track: GpxTrack,
    wayId: string,
    options: { onPickFlow?: () => void; onDone?: () => void },
  ) => void
  readonly moveExit: (track: GpxTrack, wayId: string, exitKm: number) => void
  readonly setVisible: (
    track: GpxTrack,
    wayId: string,
    visible: boolean,
  ) => void
  /**
   * Supprimer le détour source, marqueur dérivé compris.
   *
   * La liste des marqueurs y montre le marqueur de rentrée, et le supprimer
   * seul laisserait derrière lui la ligne pointillée et les points de passage
   * du détour — un détour sans sa fin, que plus rien ne permettrait d'ouvrir.
   */
  readonly delete: (track: GpxTrack, wayId: string) => void
}

interface MoveState {
  readonly track: GpxTrack
  /** `null` désigne le départ réel plutôt qu'un marqueur personnalisé. */
  readonly markerIndex: number | null
  readonly cancel: () => void
}

export class MarkerController {
  #move: MoveState | null = null
  #popup: HTMLElement | null = null
  #closePopupOnMove: (() => void) | null = null

  constructor(private readonly deps: MarkerControllerDeps) {}

  get isMoving(): boolean {
    return this.#move !== null
  }

  /* -------------------------------------------------------------------
   * Appui long : ajout d'un marqueur
   * ---------------------------------------------------------------- */

  /**
   * Propose l'ajout d'un marqueur au point touché.
   *
   * Sans effet pendant un déplacement en cours : le geste servirait alors deux
   * intentions contradictoires.
   */
  handleLongPress(lat: number, lon: number): void {
    if (this.#move !== null) return

    const t = this.deps.translator
    const tolerance = this.#toleranceMeters(lat, LONG_PRESS_TOLERANCE_PX)

    const hits = this.deps
      .tracks()
      .filter((track) => track.visible && track.allPoints.length > 0)
      .map((track) => ({
        track,
        candidates: track.projectPointAll(lat, lon, tolerance),
      }))
      .filter((hit) => hit.candidates.length > 0)

    if (hits.length === 0) {
      this.deps.shell.showSnackbar(t.t('marker_out_of_track'))
      return
    }

    if (hits.length === 1) {
      const hit = hits[0]!
      this.#addAtPressedPoint(hit.track, hit.candidates)
      return
    }

    // Plusieurs traces sous le doigt : la plus proche en premier.
    const sorted = [...hits].sort(
      (a, b) => nearest(a.candidates) - nearest(b.candidates),
    )

    chooseDialog(document.body, {
      title: t.t('long_press_choose_track'),
      cancelLabel: t.t('cancel'),
      choices: sorted.map((hit) => ({ value: hit, label: hit.track.name })),
      onChoose: (hit) => this.#addAtPressedPoint(hit.track, hit.candidates),
    })
  }

  /**
   * Ouvre l'ajout sur le passage choisi.
   *
   * Le passage entier, et non son seul kilomètre : le dialogue propose aussi
   * de poser le **départ réel**, qui ne suit pas la convention des marqueurs —
   * les deux divergent dès qu'un départ réel existe, et poser le départ avec
   * le kilomètre affiché serait faux. Android transporte pour cela deux listes
   * alignées index par index (`distanceKmOptions` et `rawDistanceKmOptions`) ;
   * ici le candidat porte les deux conventions à lui seul, et rien ne peut les
   * désaligner.
   */
  #addAtPressedPoint(
    track: GpxTrack,
    candidates: readonly DistanceCandidate[],
  ): void {
    this.#chooseCandidate(track, candidates, (candidate) =>
      this.#showAddDialog(
        track,
        track.rawDistanceToMarkerKm(candidate.distance),
        {},
        candidate.distance / 1000,
      ),
    )
  }

  /**
   * Lève l'ambiguïté quand la trace passe plusieurs fois par le point.
   *
   * Les points kilométriques sont dédoublonnés à dix mètres : deux candidats
   * plus proches que cela désignent le même endroit pour l'utilisateur.
   */
  #chooseKm(
    track: GpxTrack,
    candidates: readonly DistanceCandidate[],
    onChosen: (km: number) => void,
  ): void {
    this.#chooseCandidate(track, candidates, (candidate) =>
      onChosen(track.rawDistanceToMarkerKm(candidate.distance)),
    )
  }

  /**
   * Même choix, rendu par passage plutôt que par kilomètre.
   *
   * Ce que la liste montre reste la convention des marqueurs ; ce qu'elle rend
   * est le passage, dont on peut relire l'abscisse brute — voir
   * `#addAtPressedPoint`.
   */
  #chooseCandidate(
    track: GpxTrack,
    candidates: readonly DistanceCandidate[],
    onChosen: (candidate: DistanceCandidate) => void,
  ): void {
    const t = this.deps.translator
    const passages = distinctCandidates(track, candidates)

    if (passages.length === 0) return
    if (passages.length === 1) {
      onChosen(passages[0]!)
      return
    }

    chooseDialog(document.body, {
      title: t.t('long_press_choose_km'),
      cancelLabel: t.t('cancel'),
      choices: passages.map((candidate) => ({
        value: candidate,
        label: t.t('long_press_km_option', {
          value: t.number(track.rawDistanceToMarkerKm(candidate.distance), 2),
        }),
      })),
      onChoose: onChosen,
    })
  }

  /**
   * Choix d'un point kilométrique parmi plusieurs passages.
   *
   * Exposé pour le contrôleur des détours, qui pose lui aussi des points sur
   * une trace pouvant repasser au même endroit.
   */
  chooseKm(
    track: GpxTrack,
    candidates: readonly DistanceCandidate[],
    onChosen: (km: number) => void,
  ): void {
    this.#chooseKm(track, candidates, onChosen)
  }

  /** Variante de `#chooseKm` sur des distances brutes, sans convention. */
  #chooseRawKm(
    candidates: readonly DistanceCandidate[],
    onChosen: (km: number) => void,
  ): void {
    const t = this.deps.translator
    const kms = distinctRawKms(candidates)

    if (kms.length === 0) return
    if (kms.length === 1) {
      onChosen(kms[0]!)
      return
    }

    chooseDialog(document.body, {
      title: t.t('long_press_choose_km'),
      cancelLabel: t.t('cancel'),
      choices: kms.map((km) => ({
        value: km,
        label: t.t('long_press_km_option', { value: t.number(km, 2) }),
      })),
      onChoose: onChosen,
    })
  }

  /* -------------------------------------------------------------------
   * Dialogues d'ajout et d'édition
   * ---------------------------------------------------------------- */

  /**
   * Dialogue d'ajout.
   *
   * `realStartKm` non nul signale un point désigné sur la trace : le
   * sélecteur de type porte alors le départ réel en fin de liste, avec
   * l'abscisse brute de ce même point. La liste des marqueurs n'en fournit
   * aucun et laisse ce paramètre nul.
   */
  #showAddDialog(
    track: GpxTrack,
    distanceKm: number,
    flow: FlowCallbacks = {},
    realStartKm?: number,
  ): void {
    const t = this.deps.translator
    const form = this.#buildForm(
      track,
      {
        type: PREDEFINED_MARKER_TYPES[0]!.id,
        name: '',
        symbol: this.deps.symbolForType(PREDEFINED_MARKER_TYPES[0]!.id),
        distanceKm,
        description: '',
      },
      realStartKm,
    )

    const close = showDialog(document.body, {
      title: t.t('add_marker'),
      body: form.element,
      actions: [
        { label: t.t('cancel') },
        {
          label: t.t('marker_add'),
          primary: true,
          // Le dialogue ne se referme pas de lui-même : une distance
          // illisible ou hors trace le laissait partir avec tout ce qui avait
          // été saisi à côté, en n'offrant qu'un message. `showDialog` ferme
          // toute action qui ne demande pas à rester, si bien que le `return`
          // posé sur une valeur refusée ne retenait rien.
          keepOpen: true,
          onClick: () => {
            const values = form.read()
            if (values === null) return
            close()
            this.#commitNewMarker(track, values, flow)
          },
        },
      ],
    })
  }

  /**
   * Réglages d'un marqueur.
   *
   * `flow` est celui de la liste quand elle est ouverte dessous : régler,
   * valider ou supprimer lui rend alors la main, comme le fait un ajout. Sans
   * lui — la bulle sur la carte —, le dialogue se referme sur rien, et c'est
   * bien ce qu'on attend là.
   */
  #showEditDialog(
    track: GpxTrack,
    marker: CustomMarker,
    flow: FlowCallbacks = {},
  ): void {
    const t = this.deps.translator

    // Un marqueur de way out n'existe que comme reflet du détour : c'est le
    // détour qu'on ouvre, pas le marqueur, qui ne survivrait pas à sa
    // reconstruction.
    if (marker.type === MARKER_TYPE_WAYOUT) {
      if (marker.wayId) this.deps.ways.edit(track, marker.wayId, flow)
      return
    }

    const form = this.#buildForm(track, marker)

    const close = showDialog(document.body, {
      title: t.t('edit_marker'),
      body: form.element,
      actions: [
        {
          label: t.t('marker_delete'),
          destructive: true,
          onClick: () => this.#confirmDelete(track, marker, flow.onDone),
        },
        { label: t.t('cancel') },
        {
          label: t.t('ok'),
          primary: true,
          // Même raison qu'à l'ajout : la saisie ne se perd pas sur une faute
          // de frappe.
          keepOpen: true,
          onClick: () => {
            const values = form.read()
            if (values === null) return

            close()
            this.#replaceMarker(track, marker, {
              ...marker,
              ...values,
            })
            flow.onDone?.()
          },
        },
      ],
    })
  }

  /**
   * Construit le formulaire commun à l'ajout et à l'édition.
   *
   * Le champ symbole disparaît au profit de deux boutons radio quand le type
   * est « Dérivation » : le côté fait partie du symbole (↰ ou ↱), et le
   * saisir au clavier n'aurait aucun sens.
   *
   * `realStartKm` ajoute le pseudo-type « Départ réel » en fin de sélecteur,
   * avec l'abscisse brute du point pressé. Le choisir réduit le formulaire à
   * la distance : le départ réel n'a ni nom, ni symbole, ni description.
   */
  #buildForm(
    track: GpxTrack,
    initial: Pick<
      CustomMarker,
      'type' | 'name' | 'symbol' | 'distanceKm' | 'description'
    >,
    realStartKm?: number,
  ): {
    element: HTMLElement
    read: () => Pick<
      CustomMarker,
      'type' | 'name' | 'symbol' | 'distanceKm' | 'description'
    > | null
  } {
    const t = this.deps.translator
    const container = document.createElement('div')

    const typeSelect = document.createElement('select')
    typeSelect.className = 'field__control'
    for (const type of PREDEFINED_MARKER_TYPES) {
      const option = document.createElement('option')
      option.value = type.id
      option.textContent = t.t(type.labelKey as StringKey)
      option.selected = type.id === initial.type
      typeSelect.append(option)
    }

    // En fin de liste, après les marqueurs : ce n'en est pas un, et c'est
    // l'endroit où l'APK le pose.
    if (realStartKm !== undefined) {
      const option = document.createElement('option')
      option.value = PSEUDO_TYPE_REAL_START
      option.textContent = t.t('real_start_type')
      typeSelect.append(option)
    }

    const nameInput = textInput(initial.name)
    const symbolInput = textInput(initial.symbol)
    symbolInput.maxLength = 2

    const distanceInput = textInput(formatKmForInput(initial.distanceKm))
    distanceInput.inputMode = 'decimal'

    const descriptionInput = textInput(initial.description)

    const symbolField = field(t.t('marker_symbol'), symbolInput, t.t('marker_symbol_hint'))

    // --- Côté de la dérivation ---
    const sideField = document.createElement('div')
    sideField.className = 'field'
    const sideLabel = document.createElement('span')
    sideLabel.className = 'field__label'
    sideLabel.textContent = t.t('derivation_side_title')
    const sideGroup = document.createElement('div')
    sideGroup.className = 'field__radios'

    const sides = [
      { symbol: DERIVATION_SYMBOL_LEFT, labelKey: 'derivation_side_left' as const },
      { symbol: DERIVATION_SYMBOL_RIGHT, labelKey: 'derivation_side_right' as const },
    ]
    const sideInputs: HTMLInputElement[] = []

    for (const side of sides) {
      const wrapper = document.createElement('label')
      wrapper.className = 'field__radio'

      const radio = document.createElement('input')
      radio.type = 'radio'
      radio.name = 'derivation-side'
      radio.value = side.symbol
      radio.checked = initial.symbol === side.symbol
      sideInputs.push(radio)

      const text = document.createElement('span')
      text.textContent = `${side.symbol}  ${t.t(side.labelKey)}`

      wrapper.append(radio, text)
      sideGroup.append(wrapper)
    }

    // Aucun côté coché : on retient la gauche, comme valeur par défaut du type.
    if (!sideInputs.some((radio) => radio.checked)) {
      sideInputs[0]!.checked = true
    }

    sideField.append(sideLabel, sideGroup)

    const nameField = field(t.t('marker_name'), nameInput)
    const distance = fieldWithLabel(t.t('marker_distance_km'), distanceInput)
    const descriptionField = field(t.t('marker_description'), descriptionInput)

    // Convention actuellement affichée par le champ distance : le sélecteur
    // notifie aussi la synchronisation initiale, qui ne doit rien réécrire.
    let showingRealStart = false

    const syncType = (): void => {
      const isDerivation = typeSelect.value === MARKER_TYPE_DERIVATION
      const isRealStart = typeSelect.value === PSEUDO_TYPE_REAL_START

      // Le départ réel n'a que sa distance : ni nom, ni symbole, ni
      // description ne lui appartiennent.
      nameField.hidden = isRealStart
      descriptionField.hidden = isRealStart
      symbolField.hidden = isDerivation || isRealStart
      sideField.hidden = !isDerivation

      if (realStartKm !== undefined) {
        setDialogTitle(
          container,
          t.t(isRealStart ? 'real_start_type' : 'add_marker'),
        )
      }

      // Le champ distance change de convention avec le type : abscisse brute
      // depuis le début de la trace pour le départ réel, point kilométrique
      // des marqueurs sinon — les deux divergent dès qu'un départ réel est
      // posé. Réécrit au seul basculement, pour ne pas effacer une valeur
      // ajustée à la main.
      if (isRealStart !== showingRealStart) {
        showingRealStart = isRealStart
        distance.label.textContent = t.t(
          isRealStart ? 'real_start_distance' : 'marker_distance_km',
        )
        // `realStartKm` est défini dès que le pseudo-type existe, donc dès
        // que `isRealStart` peut être vrai ; la garde évite d'inventer un
        // zéro là où il n'y aurait rien à écrire.
        const km = isRealStart ? realStartKm : initial.distanceKm
        if (km !== undefined) distanceInput.value = formatKmForInput(km)
      }
    }

    typeSelect.addEventListener('change', () => {
      // Le symbole suit le type tant que l'utilisateur ne l'a pas personnalisé.
      const type = PREDEFINED_MARKER_TYPES.find((c) => c.id === typeSelect.value)
      if (type) symbolInput.value = this.deps.symbolForType(type.id)
      syncType()
    })
    syncType()

    container.append(
      field(t.t('marker_type'), typeSelect),
      nameField,
      symbolField,
      sideField,
      distance.element,
      descriptionField,
    )

    const read = (): Pick<
      CustomMarker,
      'type' | 'name' | 'symbol' | 'distanceKm' | 'description'
    > | null => {
      const distanceKm = parseKmInput(distanceInput.value)
      const type = typeSelect.value

      // Le départ réel se compte depuis le début de la trace : ses bornes sont
      // celles de la trace elle-même, et non celles de la convention des
      // marqueurs, qui en dérive.
      if (type === PSEUDO_TYPE_REAL_START) {
        if (distanceKm === null || !isRealStartKmValid(track, distanceKm)) {
          this.deps.shell.showSnackbar(t.t('marker_distance_invalid'))
          return null
        }
        return {
          type,
          name: '',
          symbol: '',
          distanceKm,
          description: '',
        }
      }

      // Un point kilométrique hors trace placerait le marqueur à une extrémité
      // sans que l'utilisateur comprenne pourquoi.
      const maxKm = track.rawDistanceToMarkerKm(track.totalDistance)
      if (distanceKm === null || distanceKm < 0 || distanceKm > maxKm) {
        this.deps.shell.showSnackbar(t.t('marker_distance_invalid'))
        return null
      }
      const isDerivation = type === MARKER_TYPE_DERIVATION
      const symbol = isDerivation
        ? (sideInputs.find((radio) => radio.checked)?.value ??
          DERIVATION_SYMBOL_LEFT)
        : symbolInput.value.trim().slice(0, 2)

      const label =
        PREDEFINED_MARKER_TYPES.find((c) => c.id === type)?.labelKey ??
        'marker_type_other'

      return {
        type,
        // Un marqueur sans nom reste identifiable par le libellé de son type.
        name: nameInput.value.trim() || t.t(label as StringKey),
        symbol: symbol || '!',
        distanceKm,
        description: descriptionInput.value.trim(),
      }
    }

    return { element: container, read }
  }

  /**
   * Ajoute un marqueur, en traitant l'unicité de la dérivation.
   *
   * Une seule dérivation par trace : elle marque la sortie obligatoire juste
   * avant la ligne d'arrivée, il ne peut y en avoir deux.
   */
  #commitNewMarker(
    track: GpxTrack,
    values: Pick<
      CustomMarker,
      'type' | 'name' | 'symbol' | 'distanceKm' | 'description'
    >,
    flow: FlowCallbacks = {},
  ): void {
    const t = this.deps.translator

    // Le pseudo-type ne pose pas un marqueur : il écrit le départ réel de la
    // trace. Aucun rappel de flux ne le suit — seul l'appui long le propose,
    // et il n'a rien laissé d'ouvert derrière lui.
    if (values.type === PSEUDO_TYPE_REAL_START) {
      this.#setRealStart(track, values.distanceKm)
      return
    }

    // Le type « way out » ne pose pas un marqueur : il ouvre la création
    // guidée d'un détour, dont le point kilométrique saisi est la sortie. Le
    // point de rentrée se désigne sur la carte : ce qui la couvre part avant.
    if (values.type === MARKER_TYPE_WAYOUT) {
      flow.onPickFlow?.()
      this.deps.ways.create(track, values.distanceKm, values.name || t.t('marker_type_wayout'))
      return
    }

    if (values.type === MARKER_TYPE_DERIVATION) {
      const existing = track.customMarkers.find(
        (marker) => marker.type === MARKER_TYPE_DERIVATION,
      )

      if (existing) {
        confirmDialog(document.body, {
          title: t.t('marker_type_derivation'),
          message: t.t('derivation_replace_confirm', {
            km: t.number(existing.distanceKm, 2),
          }),
          confirmLabel: t.t('derivation_replace'),
          cancelLabel: t.t('cancel'),
          onConfirm: () => {
            track.customMarkers = track.customMarkers.filter(
              (marker) => marker.type !== MARKER_TYPE_DERIVATION,
            )
            this.#appendMarker(track, values)
            flow.onDone?.()
          },
        })
        return
      }
    }

    this.#appendMarker(track, values)
    flow.onDone?.()
  }

  /**
   * Pose le départ réel désigné sur la carte.
   *
   * Une trace n'en a qu'un : en poser un second déplace le premier, après une
   * confirmation qui rappelle où il est. Écraser en silence ferait bouger les
   * points kilométriques de tous les marqueurs et des repères — c'est la même
   * règle d'unicité que la dérivation, et pour la même raison.
   */
  #setRealStart(track: GpxTrack, km: number): void {
    const t = this.deps.translator

    if (track.hasRealStart) {
      const existingKm = track.realStartDistance / 1000

      confirmDialog(document.body, {
        title: t.t('real_start_type'),
        message: t.t('real_start_replace_confirm', {
          km: t.number(existingKm, 2),
        }),
        confirmLabel: t.t('real_start_move'),
        cancelLabel: t.t('cancel'),
        onConfirm: () => this.#applyRealStart(track, km),
      })
      return
    }

    this.#applyRealStart(track, km)
  }

  /**
   * Écrit le départ réel et resynchronise ce qui en dépend.
   *
   * Deux appelants, comme `MainActivity.applyRealStart` : le déplacement du
   * marqueur de départ et le pseudo-type de l'appui long. `onTrackChanged`
   * refait les repères kilométriques, les marqueurs et le tiroir — tous
   * comptent depuis le départ réel.
   */
  #applyRealStart(track: GpxTrack, km: number): void {
    track.realStartDistance = km * 1000
    this.deps.onTrackChanged(track)

    // Trois décimales, à la différence des affichages en lecture seule :
    // c'est le seul retour après le geste, et un décalage de quelques mètres
    // ne s'y verrait pas à dix mètres près.
    this.deps.shell.showSnackbar(
      this.deps.translator.t('real_start_moved', {
        km: this.deps.translator.number(km, 3),
      }),
    )
  }

  #appendMarker(
    track: GpxTrack,
    values: Pick<
      CustomMarker,
      'type' | 'name' | 'symbol' | 'distanceKm' | 'description'
    >,
  ): void {
    track.customMarkers = [...track.customMarkers, createCustomMarker(values)]
    this.deps.onTrackChanged(track)

    this.deps.shell.showSnackbar(
      this.deps.translator.t('marker_added', {
        km: this.deps.translator.number(values.distanceKm, 2),
      }),
    )
  }

  /**
   * Remplace un marqueur.
   *
   * Comparaison par objet et non par index : la liste a pu être réordonnée
   * entre l'ouverture du dialogue et sa validation.
   */
  #replaceMarker(
    track: GpxTrack,
    previous: CustomMarker,
    next: CustomMarker,
  ): void {
    track.customMarkers = track.customMarkers.map((marker) =>
      marker === previous ? next : marker,
    )
    this.deps.onTrackChanged(track)
  }

  /**
   * Suppression confirmée d'un marqueur, d'où qu'elle soit demandée.
   *
   * Deux appelants — les réglages du marqueur et la corbeille de sa ligne
   * dans la liste —, et un seul chemin : deux suppressions indépendantes
   * finiraient par diverger sur la question posée ou sur ce qui part
   * réellement, sans que rien ne le dise.
   *
   * Un marqueur de « way out » n'est que le reflet d'un détour : le
   * supprimer seul laisserait la ligne pointillée et les points de passage
   * sans leur fin. C'est donc le détour qui part, et la question posée le
   * dit — `way_delete_confirm` plutôt que `delete_marker_confirm`, comme le
   * fait `CustomMarkerController.buildRow`.
   *
   * `onDone` sert à la liste, qui se reconstruit derrière ; les réglages,
   * eux, se referment et n'ont rien à rafraîchir.
   */
  #confirmDelete(
    track: GpxTrack,
    marker: CustomMarker,
    onDone?: () => void,
  ): void {
    const t = this.deps.translator
    const wayId = marker.type === MARKER_TYPE_WAYOUT ? marker.wayId : null

    confirmDialog(document.body, {
      title: t.t('marker_delete'),
      message: t.t(wayId ? 'way_delete_confirm' : 'delete_marker_confirm'),
      confirmLabel: t.t('marker_delete'),
      cancelLabel: t.t('cancel'),
      onConfirm: () => {
        if (wayId) {
          this.deps.ways.delete(track, wayId)
        } else {
          track.customMarkers = track.customMarkers.filter((m) => m !== marker)
          this.deps.onTrackChanged(track)
        }
        onDone?.()
      },
    })
  }

  /* -------------------------------------------------------------------
   * Bulle sur la carte
   * ---------------------------------------------------------------- */

  /**
   * Ouvre la bulle d'un marqueur.
   *
   * Trois actions, comme sur Android : déplacer, régler, fermer.
   */
  showPopup(
    track: GpxTrack,
    marker: CustomMarker,
    position: { lat: number; lon: number },
  ): void {
    const t = this.deps.translator

    this.#openPopup({
      title: marker.name,
      detail: [`${t.number(marker.distanceKm, 2)} km`, marker.description]
        .filter((line) => line.length > 0)
        .join('\n'),
      position,
      onMove: () => this.startMove(track, marker),
      onEdit: () => this.#showEditDialog(track, marker),
    })
  }

  /**
   * Bulle d'un marqueur de départ — départ, départ fictif, départ réel.
   *
   * Les trois ciblent le **départ réel** : déplacer le repose ailleurs sur la
   * trace, régler ouvre les réglages kilométriques. L'arrivée en est exclue,
   * elle ne commande rien.
   */
  showStartPopup(
    track: GpxTrack,
    position: { lat: number; lon: number },
  ): void {
    const t = this.deps.translator
    const realStartKm = track.realStartDistance / 1000

    this.#openPopup({
      title: track.name,
      detail:
        realStartKm > 0
          ? t.t('real_start_at', { value: t.number(realStartKm, 2) })
          : '',
      position,
      onMove: () => this.startRealStartMove(track),
      onEdit: () => this.deps.onEditRealStart(track),
    })
  }

  /**
   * Bulle sans commande : point d'intérêt, arrivée, radar.
   *
   * Même carte que les marqueurs, aux boutons près — c'est ce que fait
   * `MapManager.showPopup`, qui reçoit `onEdit` et `onMove` à `null` pour ces
   * entités et n'ajoute alors que la fermeture. Ces trois-là passaient encore
   * par la bulle par défaut de MapLibre, étrangère au reste de l'interface.
   */
  showLabelPopup(
    title: string,
    detail: string,
    position: { lat: number; lon: number },
  ): void {
    this.#openPopup({ title, detail, position })
  }

  #openPopup(options: {
    title: string
    detail: string
    position: { lat: number; lon: number }
    /** Absents pour une étiquette : la carte n'a alors que sa fermeture. */
    onMove?: () => void
    onEdit?: () => void
  }): void {
    this.closePopup()

    const t = this.deps.translator
    const popup = document.createElement('div')
    popup.className = 'marker-popup'

    const text = document.createElement('div')
    text.className = 'marker-popup__text'

    // Un point d'intérêt peut n'avoir qu'une description : un titre vide
    // laisserait une ligne blanche au-dessus d'elle.
    if (options.title.length > 0) {
      const title = document.createElement('div')
      title.className = 'marker-popup__title'
      title.textContent = options.title
      text.append(title)
    }

    // Une trace sans départ réel n'a rien à dire de plus : un bloc vide
    // laisserait une ligne blanche sous le titre.
    if (options.detail.length > 0) {
      const detail = document.createElement('div')
      detail.textContent = options.detail
      text.append(detail)
    }

    const actions = document.createElement('div')
    actions.className = 'marker-popup__actions'

    const action = (
      icon: Parameters<typeof createIcon>[0],
      label: string,
      onClick: () => void,
    ): HTMLButtonElement => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'marker-popup__action'
      button.title = label
      button.setAttribute('aria-label', label)
      button.append(createIcon(icon, 20))
      button.addEventListener('click', onClick)
      return button
    }

    // L'ordre d'Android : déplacer, régler, fermer — et seule la fermeture est
    // inconditionnelle.
    const onMove = options.onMove
    if (onMove) {
      actions.append(
        action('move', t.t('marker_move'), () => {
          this.closePopup()
          onMove()
        }),
      )
    }

    const onEdit = options.onEdit
    if (onEdit) {
      actions.append(
        action('settings', t.t('marker_edit_action'), () => {
          this.closePopup()
          onEdit()
        }),
      )
    }

    actions.append(action('close', t.t('close'), () => this.closePopup()))

    popup.append(text, actions)
    this.deps.shell.root.append(popup)
    this.#popup = popup

    const place = (): void => {
      const point = this.deps.map.project([
        options.position.lon,
        options.position.lat,
      ])
      // Largeur prise sur le canevas : le conteneur de MapLibre n'a pas de
      // dimensions propres et renverrait zéro.
      const width = this.deps.map.getCanvas().clientWidth

      // Bornée aux bords : une bulle à moitié hors écran est illisible.
      const half = popup.offsetWidth / 2
      const x = Math.min(Math.max(point.x, half + 8), width - half - 8)

      popup.style.left = `${x}px`
      popup.style.top = `${point.y}px`
    }

    place()

    // Fermeture à tout mouvement de caméra, comme sur Android : la bulle
    // s'ancre à un point, la laisser flotter pendant un déplacement
    // désorienterait.
    const close = (): void => this.closePopup()
    this.deps.map.once('movestart', close)
    this.#closePopupOnMove = () => this.deps.map.off('movestart', close)
  }

  closePopup(): void {
    this.#popup?.remove()
    this.#popup = null
    this.#closePopupOnMove?.()
    this.#closePopupOnMove = null
  }

  /* -------------------------------------------------------------------
   * Déplacement d'un marqueur
   * ---------------------------------------------------------------- */

  /**
   * Entre en mode déplacement.
   *
   * La carte reste manipulable : on peut zoomer et se déplacer avant de
   * désigner la nouvelle position. Un bandeau rappelle le mode et permet
   * d'abandonner.
   */
  startMove(track: GpxTrack, marker: CustomMarker): void {
    const t = this.deps.translator

    this.cancelMove(false)

    const index = track.customMarkers.indexOf(marker)
    if (index === -1) return

    const dismiss = this.deps.shell.showSnackbar(t.t('marker_move_hint'), {
      label: t.t('cancel'),
      onClick: () => this.cancelMove(true),
    })

    this.#move = { track, markerIndex: index, cancel: dismiss }
  }

  /**
   * Entre en mode déplacement du départ réel.
   *
   * Même bandeau et même geste qu'un marqueur personnalisé ; seule la valeur
   * écrite à l'arrivée diffère.
   */
  startRealStartMove(track: GpxTrack): void {
    this.cancelMove(false)

    const dismiss = this.deps.shell.showSnackbar(
      this.deps.translator.t('marker_move_hint'),
      {
        label: this.deps.translator.t('cancel'),
        onClick: () => this.cancelMove(true),
      },
    )

    this.#move = { track, markerIndex: null, cancel: dismiss }
  }

  cancelMove(notify: boolean): void {
    if (this.#move === null) return

    this.#move.cancel()
    this.#move = null

    if (notify) {
      this.deps.shell.showSnackbar(
        this.deps.translator.t('marker_move_cancelled'),
      )
    }
  }

  /** Abandonne le déplacement si la trace concernée disparaît. */
  releaseTrack(trackId: string): void {
    if (this.#move?.track.id === trackId) {
      this.cancelMove(false)
    }
  }

  /**
   * Traite un clic pendant un déplacement.
   *
   * @returns `true` si le clic a été consommé.
   */
  handleMapClick(lat: number, lon: number): boolean {
    const move = this.#move
    if (move === null) return false

    const tolerance = this.#toleranceMeters(lat, LONG_PRESS_TOLERANCE_PX)
    const candidates = move.track.projectPointAll(lat, lon, tolerance)

    // Toucher hors de la trace vaut abandon : c'est le geste naturel pour
    // sortir du mode sans chercher le bouton.
    if (candidates.length === 0) {
      this.cancelMove(true)
      return true
    }

    this.cancelMove(false)

    // Départ réel : les points kilométriques proposés sont **bruts**, comptés
    // depuis le début de la trace. `realStartDistance` ne dépend pas de
    // `markerDistanceOffset` — c'est l'inverse, l'offset en dérive — et
    // appliquer la convention marqueur ferait dépendre la valeur d'elle-même.
    if (move.markerIndex === null) {
      this.#chooseRawKm(candidates, (km) =>
        this.#applyRealStart(move.track, km),
      )
      return true
    }

    const marker = move.track.customMarkers[move.markerIndex]
    if (!marker) return true

    this.#chooseKm(move.track, candidates, (km) => {
      // Marqueur dérivé d'un détour : c'est le point de sortie du détour qui
      // se déplace, le marqueur suivra à sa reconstruction.
      if (marker.type === MARKER_TYPE_WAYOUT) {
        if (marker.wayId) this.deps.ways.moveExit(move.track, marker.wayId, km)
      } else {
        this.#replaceMarker(move.track, marker, { ...marker, distanceKm: km })
      }

      // Même raison qu'au départ réel : ce message est la seule confirmation
      // du déplacement, il doit en montrer la précision réelle.
      this.deps.shell.showSnackbar(
        this.deps.translator.t('marker_moved', {
          km: this.deps.translator.number(km, 3),
        }),
      )
    })

    return true
  }

  /* -------------------------------------------------------------------
   * Liste des marqueurs
   * ---------------------------------------------------------------- */

  /** Liste des marqueurs d'une trace, triée par point kilométrique. */
  showList(track: GpxTrack): void {
    const t = this.deps.translator
    const body = document.createElement('div')

    const render = (): void => {
      body.replaceChildren()

      const markers = [...track.customMarkers].sort(
        (a, b) => a.distanceKm - b.distanceKm,
      )

      if (markers.length === 0) {
        const empty = document.createElement('p')
        empty.style.whiteSpace = 'pre-line'
        empty.style.textAlign = 'center'
        empty.style.padding = '24px 0'
        empty.textContent = t.t('no_custom_markers')
        body.append(empty)
        return
      }

      for (const marker of markers) {
        const row = document.createElement('button')
        row.type = 'button'
        row.className = 'marker-row'
        row.dataset.visible = String(marker.visible)

        const symbol = document.createElement('span')
        symbol.className = 'marker-row__symbol'
        const symbolText = document.createElement('span')
        symbolText.textContent = marker.symbol
        symbol.append(symbolText)

        const info = document.createElement('span')
        info.className = 'marker-row__body'

        const name = document.createElement('span')
        name.className = 'marker-row__name'
        name.textContent = marker.name

        const meta = document.createElement('span')
        meta.className = 'marker-row__meta'
        meta.textContent = [
          `${t.number(marker.distanceKm, 2)} km`,
          marker.description,
        ]
          .filter((part) => part.length > 0)
          .join(' · ')

        info.append(name, meta)

        const toggle = document.createElement('span')
        toggle.className = 'marker-row__toggle'
        toggle.title = t.t('marker_visible')
        toggle.append(createIcon('visibility', 20))
        toggle.addEventListener('click', (event) => {
          event.stopPropagation()

          // La visibilité d'un marqueur dérivé appartient au détour : elle
          // commande aussi sa ligne pointillée et ses points de passage.
          if (marker.type === MARKER_TYPE_WAYOUT) {
            if (marker.wayId) {
              this.deps.ways.setVisible(track, marker.wayId, !marker.visible)
            }
          } else {
            this.#replaceMarker(track, marker, {
              ...marker,
              visible: !marker.visible,
            })
          }
          render()
        })

        // Après l'œil, en fin de ligne, comme le fait
        // `CustomMarkerController.buildRow` : la corbeille est le geste
        // destructeur, et on ne la rencontre qu'en dernier.
        const remove = document.createElement('span')
        remove.className = 'marker-row__delete'
        remove.title = t.t('marker_delete')
        remove.append(createIcon('delete', 20))
        remove.addEventListener('click', (event) => {
          // La ligne entière ouvre l'édition : sans cela, supprimer
          // ouvrirait aussi le formulaire du marqueur qu'on vient d'écarter.
          event.stopPropagation()
          this.#confirmDelete(track, marker, render)
        })

        row.append(symbol, info, toggle, remove)
        row.addEventListener('click', () => {
          // La liste ne se ferme pas derrière les réglages, pas plus que
          // derrière un ajout : régler un marqueur, ou le supprimer depuis
          // ses réglages, rend la main à la liste, qui se reconstruit. Elle
          // ne s'efface que si la suite se joue sur la carte — le détour
          // demande d'y désigner sa rentrée —, et c'est `onPickFlow` qui le
          // dit. Le marqueur de détour n'est plus traité à part ici : les
          // réglages savent le reconnaître, et deux branches jumelles
          // finiraient par diverger.
          this.#showEditDialog(track, marker, flow)
        })

        body.append(row)
      }
    }

    render()

    /**
     * Ce que les dialogues ouverts depuis la liste lui doivent.
     *
     * Deux retours possibles, et confondre les deux coûte la liste. Tant
     * qu'elle est ouverte dessous, elle se **reconstruit** sur place. Mais
     * quand la suite se joue sur la carte — changer la rentrée d'un détour, y
     * ajouter un point de passage —, elle s'efface pour libérer la carte, et
     * ce qui revient après le choix ne peut plus être un rafraîchissement :
     * son dialogue n'est plus dans le document, et le repeindre repeindrait
     * un nœud détaché, sans que rien ne le signale. Elle se **rouvre** donc.
     */
    let closedForPick = false
    const flow: FlowCallbacks = {
      onPickFlow: () => {
        closedForPick = true
        close()
      },
      onDone: () => {
        if (closedForPick) this.showList(track)
        else render()
      },
    }

    const close = showDialog(document.body, {
      title: t.t('custom_markers'),
      body,
      actions: [
        {
          // La liste ne se ferme pas derrière l'ajout : une feuille de route
          // se dépouille marqueur par marqueur — GPM, sprints, ravitos —, et
          // rouvrir le menu, les réglages puis la liste entre chacun rend
          // l'enchaînement impraticable. Android tient la liste ouverte de la
          // même façon et la reconstruit après chaque ajout.
          label: t.t('marker_add'),
          keepOpen: true,
          onClick: () => this.#showAddDialog(track, 0, flow),
        },
        { label: t.t('close') },
      ],
    })
  }

  /* -------------------------------------------------------------------
   * Utilitaires
   * ---------------------------------------------------------------- */

  /** Tolérance d'un geste, convertie de pixels en mètres au sol. */
  #toleranceMeters(lat: number, pixels: number): number {
    return metersPerPixel(this.deps.map.getZoom(), lat) * pixels
  }

  /** Rayon de recherche d'un marqueur au clic, en pixels. */
  static get clickTolerancePx(): number {
    return CLICK_TOLERANCE_PX
  }
}

/* ---------------------------------------------------------------------
 * Fabriques de champs
 * ------------------------------------------------------------------ */

function textInput(value: string): HTMLInputElement {
  const input = document.createElement('input')
  input.type = 'text'
  input.className = 'field__control'
  input.value = value
  return input
}

/**
 * Champ dont le libellé reste accessible.
 *
 * Celui de la distance change avec le type choisi — « Distance (km) » pour un
 * marqueur, « Distance de départ réel (km) » pour le départ réel, les deux ne
 * comptant pas depuis le même point. Le rendre plutôt que d'aller le rechercher
 * dans le champ évite une requête de sélecteur qui pourrait ne rien trouver.
 */
function fieldWithLabel(
  label: string,
  control: HTMLElement,
  hint?: string,
): { element: HTMLElement; label: HTMLElement } {
  const wrapper = document.createElement('label')
  wrapper.className = 'field'

  const text = document.createElement('span')
  text.className = 'field__label'
  text.textContent = label

  wrapper.append(text, control)

  if (hint !== undefined) {
    const hintElement = document.createElement('span')
    hintElement.className = 'field__hint'
    hintElement.textContent = hint
    wrapper.append(hintElement)
  }

  return { element: wrapper, label: text }
}

function field(
  label: string,
  control: HTMLElement,
  hint?: string,
): HTMLElement {
  return fieldWithLabel(label, control, hint).element
}

/* ---------------------------------------------------------------------
 * Formatage des distances
 * ------------------------------------------------------------------ */

/**
 * Point kilométrique tel qu'il apparaît dans le champ de saisie.
 *
 * Trois décimales, soit une précision au mètre, zéros de fin retirés : le
 * champ affiche « 12.5 » et non « 12.500 ».
 */
export function formatKmForInput(km: number): string {
  const fixed = km.toFixed(3)
  return fixed.includes('.')
    ? fixed.replace(/0+$/, '').replace(/\.$/, '')
    : fixed
}

/**
 * Analyse une distance saisie.
 *
 * La virgule est acceptée comme séparateur décimal : c'est ce que produit un
 * clavier français.
 */
export function parseKmInput(text: string): number | null {
  const normalized = text.trim().replace(',', '.')
  if (normalized === '') return null

  const value = Number.parseFloat(normalized)
  return Number.isFinite(value) ? value : null
}

/**
 * Passages distincts correspondant à un point de l'écran.
 *
 * Deux passages plus proches que dix mètres désignent le même endroit pour
 * l'utilisateur, et proposer un choix entre eux n'apporterait rien. Le
 * dédoublonnage se fait sur le point kilométrique **affiché**, celui du choix
 * offert.
 *
 * Rend les passages et non leurs kilomètres : le même passage se lit dans deux
 * conventions — celle des marqueurs et l'abscisse brute du départ réel —, et
 * l'appui long a besoin des deux. Les tirer d'un seul objet vaut mieux que
 * d'aligner deux listes, comme le fait l'APK : rien ne peut les désaligner.
 */
export function distinctCandidates(
  track: GpxTrack,
  candidates: readonly DistanceCandidate[],
): DistanceCandidate[] {
  const firstOfEachSlice = new Map<number, DistanceCandidate>()

  for (const candidate of candidates) {
    const slice = Math.trunc(
      track.rawDistanceToMarkerKm(candidate.distance) * 100,
    )
    if (!firstOfEachSlice.has(slice)) firstOfEachSlice.set(slice, candidate)
  }

  // Tri par abscisse brute : la convention des marqueurs n'en est qu'une
  // translation, l'ordre est le même.
  return [...firstOfEachSlice.values()].sort((a, b) => a.distance - b.distance)
}

/**
 * Points kilométriques distincts correspondant à un point de l'écran.
 *
 * Même dédoublonnage que `distinctCandidates`, dont ce n'est que la lecture en
 * convention marqueurs.
 */
export function distinctMarkerKms(
  track: GpxTrack,
  candidates: readonly DistanceCandidate[],
): number[] {
  return distinctCandidates(track, candidates).map((candidate) =>
    track.rawDistanceToMarkerKm(candidate.distance),
  )
}

/**
 * Le kilomètre saisi peut-il être le départ réel de cette trace ?
 *
 * Il se compte depuis le début de la trace : avant zéro ou après la fin, il ne
 * désigne aucun point, et le marqueur de départ irait se poser à une extrémité
 * sans que rien ne l'explique. La convention des marqueurs ne s'applique pas —
 * c'est elle qui dérive de cette valeur.
 */
export function isRealStartKmValid(track: GpxTrack, km: number): boolean {
  return km >= 0 && km <= track.totalDistance / 1000
}

/**
 * Points kilométriques bruts distincts, comptés depuis le début de la trace.
 *
 * Sert au déplacement du départ réel, qui précède justement toute convention
 * de comptage. Même dédoublonnage à dix mètres que `distinctMarkerKms`.
 */
export function distinctRawKms(
  candidates: readonly DistanceCandidate[],
): number[] {
  return distinctByTenMetres(
    candidates.map((candidate) => candidate.distance / 1000),
  )
}

/**
 * Dédoublonne à dix mètres **sans arrondir la valeur retenue**.
 *
 * C'est le regroupement qui se fait à dix mètres, jamais la valeur : elle
 * pré-remplit un champ d'édition, et un arrondi s'y écrirait dans le marqueur
 * au premier « OK ». Le portage arrondissait, ce qui interdisait de poser ou
 * de déplacer un marqueur ailleurs qu'au décamètre — `CustomMarkerController`
 * et `MainActivity.formatKmForInput` posent l'inverse : affichage en lecture
 * seule à 10 m, champ d'édition au mètre.
 *
 * La troncature, et non l'arrondi, reprend le `distinctBy { (it * 100).toLong() }`
 * de `MapManager.handleTrackLongPress` : les tranches de dix mètres y sont
 * fixes plutôt que centrées.
 */
function distinctByTenMetres(kms: readonly number[]): number[] {
  const firstOfEachSlice = new Map<number, number>()

  for (const km of kms) {
    const slice = Math.trunc(km * 100)
    if (!firstOfEachSlice.has(slice)) firstOfEachSlice.set(slice, km)
  }

  return [...firstOfEachSlice.values()].sort((a, b) => a - b)
}

function nearest(candidates: readonly DistanceCandidate[]): number {
  return candidates.reduce(
    (best, candidate) => Math.min(best, candidate.projDist),
    Number.POSITIVE_INFINITY,
  )
}
